<!-- ═══ Add Bar ═══ -->
<div class="mb-3 flex flex-col gap-1.5">
  <div class="flex gap-1">
    @for (opt of addCategoryOptions; track opt.value) {
      <button
        class="rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors"
        [class]="
          addCategory() === opt.value
            ? 'bg-sky-100 text-sky-700'
            : 'bg-neutral-100 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-600'
        "
        (click)="addCategory.set(opt.value)"
      >
        {{ opt.label }}
      </button>
    }
  </div>
  <div class="flex items-end gap-1.5">
    @switch (addCategory()) {
      @case ('element') {
        <dojo-single-select
          [formCtrl]="newEntryTypeCtrl"
          [options]="elementAddOptions"
          optionLabel="label"
          optionValue="value"
          [size]="'s'"
          placeholder="Type..."
          class="flex-1"
        />
        <dojo-text-input
          [formCtrl]="newEntryIdCtrl"
          [size]="'s'"
          [placeholder]="defaultNewId() || 'ID'"
          class="w-16"
          (keydown.enter)="onAddElement()"
        />
        <dojo-button [size]="'s'" customColor="sky" (click)="onAddElement()">Add</dojo-button>
      }
      @case ('interaction') {
        <dojo-single-select
          [formCtrl]="newControlTypeCtrl"
          [options]="interactionAddOptions"
          optionLabel="label"
          optionValue="value"
          [size]="'s'"
          placeholder="Type..."
          class="flex-1"
        />
        @if (newControlTypeCtrl.value !== 'reaction') {
          <dojo-text-input
            [formCtrl]="newControlIdCtrl"
            [size]="'s'"
            placeholder="ID"
            class="w-16"
            (keydown.enter)="onAddInteraction()"
          />
        }
        <dojo-button [size]="'s'" customColor="sky" (click)="onAddInteraction()">Add</dojo-button>
      }
    }
  </div>
</div>

<!-- ═══════════════════════════════════════════════════════════════════ -->
<!-- GLOBAL                                                             -->
<!-- ═══════════════════════════════════════════════════════════════════ -->
<div class="mb-1 mt-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Global</div>
<mat-accordion class="custom-mat-accordion" [multi]="true">
  <!-- ═══ Canvas ═══ -->
  <mat-expansion-panel>
    <mat-expansion-panel-header>
      <mat-panel-title class="flex items-center gap-2 text-sm">
        <span class="font-medium">Canvas</span>
        <span class="rounded bg-neutral-100 px-1.5 py-px text-[10px] text-neutral-400">canvas</span>
      </mat-panel-title>
      <mat-panel-description class="!flex-grow-0"><div class="w-5"></div></mat-panel-description>
    </mat-expansion-panel-header>

    <dojo-canvas-editor [canvasConfig]="display()?.canvas" (canvasConfigChange)="onCanvasConfigChange($event)">
      <dojo-number-input
        label="Precision"
        [formCtrl]="defaultPrecisionCtrl"
        [step]="1"
        [min]="0"
        [max]="10"
        [size]="'s'"
      />
    </dojo-canvas-editor>
  </mat-expansion-panel>

  <!-- ═══ Coordinate System ═══ -->
  <mat-expansion-panel>
    <mat-expansion-panel-header>
      <mat-panel-title class="flex items-center gap-2 text-sm">
        <span class="font-medium">Coordinate System</span>
        <span class="rounded bg-neutral-100 px-1.5 py-px text-[10px] text-neutral-400">coord</span>
      </mat-panel-title>
      <mat-panel-description class="!flex-grow-0"><div class="w-5"></div></mat-panel-description>
    </mat-expansion-panel-header>

    <dojo-coordinate-system-editor [coordSystem]="coordSystem()" (coordSystemChange)="coordSystemChange.emit($event)" />
  </mat-expansion-panel>
</mat-accordion>

<!-- ═══════════════════════════════════════════════════════════════════ -->
<!-- ELEMENTS                                                           -->
<!-- ═══════════════════════════════════════════════════════════════════ -->
@if (elementEntries().length) {
  <div class="mb-1 mt-4 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Elements</div>
  <mat-accordion class="custom-mat-accordion" [multi]="true">
    @for (entry of elementEntries(); track entry.trackId) {
      <mat-expansion-panel>
        <mat-expansion-panel-header>
          <mat-panel-title class="flex items-center gap-2 text-sm">
            <span class="font-medium">{{ entry.displayName }}</span>
            <span class="rounded bg-neutral-100 px-1.5 py-px text-[10px] text-neutral-400">{{ entry.badge }}</span>
          </mat-panel-title>
          <mat-panel-description class="!mr-0 !flex-grow-0">
            <dojo-icon
              icon="trash"
              [size]="12"
              class="cursor-pointer text-sky-400 hover:text-sky-600"
              (click)="onDeleteEntry(entry); $event.stopPropagation()"
            />
          </mat-panel-description>
        </mat-expansion-panel-header>

        <div class="p-4">
          @if (entry.def) {
            <dojo-element-editor
              [elementId]="entry.id"
              [elementDef]="entry.def"
              [elementDisplay]="entry.display"
              [functionElementIds]="functionElementIds()"
              [allElementIds]="allElementIds()"
              (paramsChange)="onElementParamsChange(entry.id, entry, $event)"
              (displayChange)="onElementDisplayChange(entry.id, $event)"
            />
          }
        </div>
      </mat-expansion-panel>
    }
  </mat-accordion>
}

<!-- ═══════════════════════════════════════════════════════════════════ -->
<!-- INTERACTIONS                                                       -->
<!-- ═══════════════════════════════════════════════════════════════════ -->
@if (controlEntries().length || reactions().length) {
  <div class="mb-1 mt-4 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Interactions</div>
  <div class="flex flex-col gap-1">
    @if (controlEntries().length) {
      <mat-accordion class="custom-mat-accordion" [multi]="true">
        @for (entry of controlEntries(); track entry.trackId) {
          <mat-expansion-panel>
            <mat-expansion-panel-header>
              <mat-panel-title class="flex items-center gap-2 text-sm">
                <span class="font-medium">{{ entry.displayName }}</span>
                <span class="rounded bg-neutral-100 px-1.5 py-px text-[10px] text-neutral-400">{{ entry.badge }}</span>
              </mat-panel-title>
              <mat-panel-description class="!mr-0 !flex-grow-0">
                <dojo-icon
                  icon="trash"
                  [size]="12"
                  class="cursor-pointer text-sky-400 hover:text-sky-600"
                  (click)="onDeleteEntry(entry); $event.stopPropagation()"
                />
              </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="p-4">
              <dojo-control-fields-editor
                [control]="entry.control!"
                [elementIds]="allElementIds()"
                (controlChange)="onControlChange(entry.controlIndex!, $event)"
              />
            </div>
          </mat-expansion-panel>
        }
      </mat-accordion>
    }
    <dojo-reaction-list-editor
      [reactions]="reactions()"
      [elementIds]="allElementIds()"
      (reactionsChange)="reactionsChange.emit($event)"
    />
  </div>
}
